// call-tones.js — v4.9.3 : sonnerie locale et retour de sonnerie WebRTC.
// Aucun fichier audio distant : les tonalités sont synthétisées via Web Audio.
// Cela ne transporte aucune donnée et ne modifie pas le chiffrement des appels.

let audioContext = null;
let activeStop = null;

const PATTERNS = Object.freeze({
  incoming: Object.freeze({ intervalMs: 2800, durationMs: 900, frequencies: [440, 480], gain: 0.055, vibrate: [260, 120, 260] }),
  outgoing: Object.freeze({ intervalMs: 2600, durationMs: 700, frequencies: [440, 480], gain: 0.025, vibrate: [] }),
});

function contextCtor() {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext || null;
}

export async function primeCallAudio() {
  const Ctor = contextCtor();
  if (!Ctor) return false;
  try {
    if (!audioContext || audioContext.state === 'closed') audioContext = new Ctor();
    if (audioContext.state === 'suspended') await audioContext.resume();
    // Une impulsion muette amorce proprement Web Audio pendant un geste utilisateur
    // sur Safari/iOS sans produire de son parasite.
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    gain.gain.value = 0;
    osc.connect(gain); gain.connect(audioContext.destination);
    osc.start(); osc.stop(audioContext.currentTime + 0.015);
    return true;
  } catch (_) { return false; }
}

function pulse(pattern) {
  const ctx = audioContext;
  if (!ctx || ctx.state !== 'running') return;
  const now = ctx.currentTime;
  const end = now + Math.max(0.08, pattern.durationMs / 1000);
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(pattern.gain, now + 0.025);
  master.gain.setValueAtTime(pattern.gain, Math.max(now + 0.03, end - 0.045));
  master.gain.exponentialRampToValueAtTime(0.0001, end);
  master.connect(ctx.destination);
  for (const frequency of pattern.frequencies) {
    const osc = ctx.createOscillator();
    osc.type = 'sine'; osc.frequency.value = frequency;
    osc.connect(master); osc.start(now); osc.stop(end + 0.01);
  }
  if (pattern.vibrate?.length && typeof navigator !== 'undefined') {
    try { navigator.vibrate?.(pattern.vibrate); } catch (_) {}
  }
}

export function stopCallTone() {
  if (activeStop) {
    const stop = activeStop; activeStop = null;
    try { stop(); } catch (_) {}
  }
  if (typeof navigator !== 'undefined') {
    try { navigator.vibrate?.(0); } catch (_) {}
  }
}

export function startCallTone(kind = 'incoming') {
  stopCallTone();
  const pattern = PATTERNS[kind] || PATTERNS.incoming;
  let stopped = false;
  let interval = null;
  const run = async () => {
    const ok = await primeCallAudio();
    if (stopped || !ok) return;
    pulse(pattern);
    interval = setInterval(() => { if (!stopped) pulse(pattern); }, pattern.intervalMs);
  };
  run().catch(() => {});
  const stop = () => { stopped = true; if (interval) clearInterval(interval); interval = null; };
  activeStop = stop;
  return () => {
    if (activeStop === stop) activeStop = null;
    stop();
    if (typeof navigator !== 'undefined') { try { navigator.vibrate?.(0); } catch (_) {} }
  };
}

export const __test = { PATTERNS };
